import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Grid from '@anwb/poncho/components/grid';
import Notification from '@anwb/poncho/components/notification';
import PonchoThankYou from '@anwb/poncho/components/thank-you';
import Typography from '@anwb/poncho/components/typography';
import {
	resetAgreedToTerms,
	resetChosenLeaseCar,
	resetCustomer,
	resetLeaseCarData,
	resetRemarks,
	setCustomerDataFilledFromLogin,
} from 'store/actions';
import {
	isFlexSelector,
	isOccasionSelector,
	plCustomerSelector,
	stateSelector,
} from 'store/selectors';
import { PLCustomerGender } from 'types/plCustomerData';
import { staticAssetsUrl } from 'utils/constants';
import { thankYouValidator } from 'utils/stepValidators/thankYouValidator';

import { InAssociationFooter, InAssociationImage } from './ThankYou.styled';
import {
	flexLeaseExpectations,
	newLeaseExpectations,
	occasionLeaseExpectations,
} from './expectations';

type DisplayData = {
	customer: {
		gender?: PLCustomerGender;
		lastNamePrefix?: string;
		lastName?: string;
	};
};

type ThankYouProps = {
	onErrorChange: (hasError: boolean) => void;
};

export const ThankYou: FunctionComponent<ThankYouProps> = ({ onErrorChange }) => {
	const { gender, lastNamePrefix, lastName } = useSelector(plCustomerSelector);
	const state = useSelector(stateSelector);
	const isOccasion = useSelector(isOccasionSelector);
	const isFlex = useSelector(isFlexSelector);
	const [displayData, setDisplayData] = useState<DisplayData | null>(null);
	const [showNotifications, setShowNotifications] = useState<boolean>(false);
	const dispatch = useDispatch();
	const AyvensLogo = new URL('/images/ayvens.png', staticAssetsUrl()).href;

	useEffect(() => {
		if (!thankYouValidator(state)) {
			onErrorChange(true);
			setShowNotifications(true);
		} else {
			onErrorChange(false);
		}

		setDisplayData({
			customer: {
				gender,
				lastName,
				lastNamePrefix,
			},
		});

		window.scrollTo(0, 0);
		sessionStorage.removeItem('anwb-private-lease');
		sessionStorage.removeItem('login-or-sign-up-membership');
		dispatch(resetRemarks());
		dispatch(resetAgreedToTerms());
		dispatch(resetChosenLeaseCar());
		dispatch(resetCustomer());
		dispatch(resetLeaseCarData());
		dispatch(setCustomerDataFilledFromLogin(false));
		// Disable dependency array, useEffect needs to run once.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (showNotifications)
		return (
			<Grid.Item inline fullWidth>
				<Notification title="Deze pagina bestaat helaas niet meer" variant="warning">
					De aanvraag is ook terug te vinden in je mail. Lees{' '}
					<Typography
						variant="link-text"
						href="/auto/private-lease/anwb-private-lease/hoe-werkt-het"
					>
						hier
					</Typography>{' '}
					ook meer informatie over hoe ANWB Private Lease werkt.
				</Notification>
			</Grid.Item>
		);

	const expectations = () => {
		if (isOccasion) return occasionLeaseExpectations;
		if (isFlex) return flexLeaseExpectations;
		return newLeaseExpectations;
	};
	return (
		<>
			<PonchoThankYou
				dataTest="private-lease-thank-you"
				salutation={displayData?.customer.gender === 'FEMALE' ? 'm' : 'h'}
				surnamePrefix={displayData?.customer.lastNamePrefix}
				surname={displayData?.customer.lastName || ''}
				intro={
					isFlex
						? 'Fijn dat je geïnteresseerd bent in Flex Lease! Je aanvraag wordt zo snel mogelijk verwerkt.'
						: `Fijn dat je geïnteresseerd bent in Private Lease${
								isOccasion ? ' Occasion' : ''
							}! Je aanvraag wordt zo snel mogelijk verwerkt.`
				}
				expectations={expectations()}
			/>

			<InAssociationFooter>
				<Typography variant="content-title">Heb je nog vragen?</Typography>
				<Typography>
					Neem dan contact op met ons Leden Service Center op{' '}
					<Typography variant="link-text" href="tel:+31882692020">
						088 269 20 20
					</Typography>{' '}
					(maandag t/m vrijdag 8.30 - 18.00 uur). Of bekijk{' '}
					<Typography
						variant="link-text"
						href="/auto/private-lease/anwb-private-lease"
						target="_blank"
						rel="noreferrer"
					>
						ANWB Private Lease
					</Typography>
					.
				</Typography>
			</InAssociationFooter>
			<InAssociationFooter>
				<Typography variant="usp-text-md">
					In samenwerking met:
					<InAssociationImage
						id="ayvens-logo"
						class="in-association-image"
						src={AyvensLogo}
						alt="Ayvens"
					/>
				</Typography>
			</InAssociationFooter>
		</>
	);
};
